import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

const PurchaseOrderReceptionModal = ({ order, isOpen, onClose, onUpdated }) => {
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();
    const [items, setItems] = useState([]);
    const [deliveryDate, setDeliveryDate] = useState('');
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
    const [activeTab, setActiveTab] = useState('commande');
    const [isCorrecting, setIsCorrecting] = useState(false);

    useEffect(() => {
        if (isOpen && order) {
            setActiveTab(order.status === 'Brouillon' ? 'historique' : 'commande');
            setItems((order.items || []).map(i => ({
                ...i,
                previouslyReceived: i.receivedQty || 0,
                currentReceivedQty: i.receivedQty || 0
            })));
            setDeliveryDate(order.deliveryDate ? order.deliveryDate.split('T')[0] : new Date().toISOString().split('T')[0]);

            if (user?.companyId) {
                fetch(`/api/warehouses?companyId=${user.companyId}`)
                    .then(res => res.json())
                    .then(data => {
                        setWarehouses(data);
                        if (data.length > 0) setSelectedWarehouseId(data[0].id);
                    });
            }
        }
    }, [isOpen, order, user]);

    if (!isOpen || !order) return null;

    const isClosed = order.status === 'Clôturée';
    // Mode vue/lecture seule ?
    const isReadOnly = isClosed && !isCorrecting;

    const updateReceivedQty = (itemId, qty) => {
        setItems(items.map(i => {
            if (i.id !== itemId) return i;
            // Can go down to 0, but can't exceed ordered qty
            const clamped = Math.max(0, Math.min(qty, i.quantity));
            return { ...i, currentReceivedQty: clamped };
        }));
    };

    const handleReceiveAll = () => {
        setItems(items.map(i => ({ ...i, currentReceivedQty: i.quantity })));
    };

    const handleClearAll = () => {
        setItems(items.map(i => ({ ...i, currentReceivedQty: 0 })));
    };

    const totalOrdered = items.reduce((s, i) => s + i.quantity, 0);
    const totalCurrentReceived = items.reduce((s, i) => s + i.currentReceivedQty, 0);
    const totalPreviouslyReceived = items.reduce((s, i) => s + i.previouslyReceived, 0);

    // hasChanges covers both positive and negative diffs
    const hasChanges = items.some(i => i.currentReceivedQty !== i.previouslyReceived);
    const diffTotal = totalCurrentReceived - totalPreviouslyReceived;
    const allFullyReceived = items.every(i => i.currentReceivedQty >= i.quantity);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!hasChanges) {
            showToast("Aucune modification à enregistrer", "warning");
            return;
        }

        if (!selectedWarehouseId) {
            showToast("Veuillez sélectionner un entrepôt de réception", "error");
            return;
        }

        let confirmMsg = 'Confirmez-vous cette réception partielle ?';
        if (allFullyReceived && diffTotal > 0) confirmMsg = 'Confirmez-vous la réception totale ? Cela clôturera la commande.';
        if (diffTotal < 0 || isCorrecting) confirmMsg = 'Attention, vous êtes en train de corriger cette réception à la baisse. Confirmez-vous la retirer du stock réel ?';

        showConfirm({
            title: diffTotal < 0 ? 'Valider la correction ?' : 'Valider la réception ?',
            message: confirmMsg,
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/purchase-orders/${order.id}/receive`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            items: items.map(i => ({ id: i.id, receivedQty: i.currentReceivedQty, productId: i.productId })),
                            deliveryDate,
                            warehouseId: selectedWarehouseId,
                            companyId: user.companyId,
                            userId: user.id
                        })
                    });

                    if (res.ok) {
                        const data = await res.json();
                        const msg = data.status === 'Clôturée'
                            ? "Commande clôturée — stock mis à jour"
                            : "Réception partielle enregistrée";
                        showToast(msg, "success");
                        onUpdated();
                        onClose();
                    } else {
                        const data = await res.json();
                        showToast(data.error || "Erreur lors de la mise à jour", "error");
                    }
                } catch (error) {
                    showToast("Erreur serveur", "error");
                }
            }
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 w-full max-w-4xl flex flex-col max-h-[95vh] border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">

                {/* Premium Header with Gradient */}
                <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                    <div className="relative z-10 flex justify-between items-center">
                        <div className="flex gap-4 items-center">
                            <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center shadow-inner">
                                <span className="material-symbols-outlined text-primary text-[28px]">inventory_2</span>
                            </div>
                            <div>
                                <h2 className="text-xl font-black text-slate-900 dark:text-white leading-tight">
                                    Réception Commande <span className="text-primary">{order.reference}</span>
                                </h2>
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] opacity-70">
                                    Fournisseur : {order.supplier?.name}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                {/* Tabs Premium */}
                <div className="flex px-8 gap-8 border-b border-slate-100 dark:border-slate-800 shrink-0 bg-slate-50/30 dark:bg-slate-800/10">
                    <button
                        onClick={() => order.status !== 'Brouillon' && setActiveTab('commande')}
                        disabled={order.status === 'Brouillon'}
                        className={`relative py-4 text-[11px] font-black uppercase tracking-[0.2em] transition-all ${activeTab === 'commande' ? 'text-primary' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'} ${order.status === 'Brouillon' ? 'opacity-30 cursor-not-allowed' : ''}`}
                    >
                        <span className="flex items-center gap-2.5">
                            <span className="material-symbols-outlined text-[20px]">inventory_2</span>
                            Réception
                        </span>
                        {activeTab === 'commande' && (
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-t-full animate-in slide-in-from-bottom-2" />
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('historique')}
                        className={`relative py-4 text-[11px] font-black uppercase tracking-[0.2em] transition-all ${activeTab === 'historique' ? 'text-primary' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'}`}
                    >
                        <span className="flex items-center gap-2.5">
                            <span className="material-symbols-outlined text-[20px]">history</span>
                            Historique
                        </span>
                        {activeTab === 'historique' && (
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-t-full animate-in slide-in-from-bottom-2" />
                        )}
                    </button>
                </div>

                {activeTab === 'historique' && (
                    <div className="flex-1 overflow-y-auto p-10 bg-slate-50/30 dark:bg-slate-900/50 custom-scrollbar">
                        {(!order.statusHistory || order.statusHistory.length === 0) ? (
                            <div className="text-center py-20 px-10 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[2rem]">
                                <span className="material-symbols-outlined text-4xl text-slate-200 mb-4">history</span>
                                <p className="text-sm font-bold text-slate-400 italic">Aucun historique disponible pour cette commande.</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {[...order.statusHistory].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((hist, idx) => (
                                    <div key={hist.id} className="relative pl-10 pb-8 border-l-2 border-slate-100 dark:border-slate-800 last:border-transparent last:pb-0">
                                        {/* Dot */}
                                        <div className="absolute -left-[9px] top-0 size-4 rounded-full bg-white dark:bg-slate-900 border-2 border-primary shadow-[0_0_10px_rgba(var(--primary-rgb),0.3)] transition-all hover:scale-125 hover:shadow-[0_0_15px_rgba(var(--primary-rgb),0.5)] cursor-default" />
                                        <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700/50 p-6 rounded-[1.5rem] shadow-sm hover:shadow-xl hover:border-primary/20 transition-all group">
                                            <div className="flex items-start justify-between gap-4 mb-2">
                                                <div className="flex items-center gap-2.5">
                                                    <span className="px-2 py-1 bg-slate-100 dark:bg-slate-900 rounded font-bold uppercase tracking-wider border border-slate-200 dark:border-slate-700 text-[10px] text-slate-700 dark:text-slate-300 shadow-sm">
                                                        {hist.newStatus}
                                                    </span>
                                                    <span className="text-sm font-medium text-slate-900 dark:text-white leading-tight">{hist.notes || 'Modification de statut'}</span>
                                                </div>
                                                <span className="text-[10px] font-mono text-slate-400 font-bold whitespace-nowrap">
                                                    {new Date(hist.createdAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 text-slate-500 text-[11px] font-bold uppercase tracking-widest">
                                                {hist.user?.avatar ? (
                                                    <img src={hist.user.avatar} alt={hist.user.name} className="w-5 h-5 rounded-full object-cover border border-slate-200 dark:border-slate-700" />
                                                ) : (
                                                    <div className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 flex items-center justify-center text-[8px]">
                                                        {hist.user?.name?.[0]?.toUpperCase()}
                                                    </div>
                                                )}
                                                {hist.user?.name || 'Système'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'commande' && (
                    <div className="flex-1 flex flex-col min-h-0">
                        {/* Banner for closed orders */}
                        {isClosed && !isCorrecting && (
                            <div className="mx-8 mt-6 p-6 bg-emerald-500/10 border border-emerald-500/20 rounded-3xl flex items-start gap-6 shrink-0 animate-in fade-in slide-in-from-top-4">
                                <div className="size-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center shrink-0">
                                    <span className="material-symbols-outlined text-emerald-500 text-[28px]">verified</span>
                                </div>
                                <div>
                                    <p className="font-black text-emerald-700 dark:text-emerald-400 text-sm uppercase tracking-wider mb-1">Commande clôturée</p>
                                    <p className="text-[13px] text-emerald-600 dark:text-emerald-500/80 mb-4 font-medium leading-relaxed">Tous les articles ont été réceptionnés. Le stock a été mis à jour dans vos différents entrepôts.</p>
                                    <button
                                        type="button"
                                        onClick={() => setIsCorrecting(true)}
                                        className="text-[10px] font-black uppercase tracking-widest px-5 h-9 bg-white dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 rounded-xl hover:bg-emerald-500 hover:text-white dark:hover:bg-emerald-500 transition-all shadow-sm"
                                    >
                                        Corriger une erreur sur cette réception
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Banner for correction mode */}
                        {isCorrecting && (
                            <div className="mx-8 mt-6 bg-amber-500/10 border border-amber-500/20 rounded-3xl p-6 flex items-start gap-6 shrink-0 animate-in fade-in slide-in-from-top-4">
                                <div className="size-12 rounded-2xl bg-amber-500/20 flex items-center justify-center shrink-0">
                                    <span className="material-symbols-outlined text-amber-500 text-[28px]">warning_amber</span>
                                </div>
                                <div>
                                    <p className="font-black text-amber-700 dark:text-amber-400 text-sm uppercase tracking-wider mb-1">Mode Correction Activé</p>
                                    <p className="text-[13px] text-amber-600 dark:text-amber-500/80 font-medium leading-relaxed">Vous pouvez réduire les quantités réceptionnées. Le stock lié sera diminué, et la commande pourra être ré-ouverte automatiquement.</p>
                                </div>
                            </div>
                        )}

                        {/* Progress summary Premium */}
                        <div className="mx-8 mt-8 p-6 bg-white dark:bg-slate-800/50 rounded-[2rem] border-2 border-slate-100 dark:border-slate-800 shrink-0 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className={`size-8 rounded-lg flex items-center justify-center ${allFullyReceived ? 'bg-emerald-500/10 text-emerald-500' : 'bg-primary/10 text-primary'}`}>
                                        <span className="material-symbols-outlined text-[20px]">{allFullyReceived ? 'verified' : 'pending_actions'}</span>
                                    </div>
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Progression globale</span>
                                </div>
                                <span className="font-mono font-black text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-900 px-3 py-1 rounded-full text-xs">
                                    {totalCurrentReceived} <span className="text-slate-400 mx-1">/</span> {totalOrdered}
                                </span>
                            </div>
                            <div className="h-2.5 bg-slate-100 dark:bg-slate-900 rounded-full overflow-hidden shadow-inner border border-slate-50 dark:border-slate-800">
                                <div
                                    className={`h-full rounded-full transition-all duration-1000 ${allFullyReceived ? 'bg-emerald-500' : 'bg-primary'} shadow-[0_0_15px_rgba(var(--primary-rgb),0.4)]`}
                                    style={{ width: `${totalOrdered > 0 ? (totalCurrentReceived / totalOrdered) * 100 : 0}%` }}
                                />
                            </div>
                        </div>


                        {/* Body (Scrollable items) Premium */}
                        <div className="flex-1 overflow-y-auto p-10 pt-8 custom-scrollbar">
                            <div className="flex justify-between items-center mb-6">
                                <h4 className="font-black text-slate-900 dark:text-white flex items-center gap-3 text-[10px] uppercase tracking-[0.2em]">
                                    <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-primary text-[20px]">list_alt</span>
                                    </div>
                                    Lignes de commande
                                    <span className="size-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-black text-slate-500">{items.length}</span>
                                </h4>
                                {!isClosed && !isCorrecting && (
                                    <div className="flex gap-4">
                                        <button type="button" onClick={handleClearAll} className="h-9 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 border-amber-200 text-amber-600 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-all active:scale-95 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-[18px]">block</span>
                                            Tout vider
                                        </button>
                                        <button type="button" onClick={handleReceiveAll} className="h-9 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest bg-emerald-500 text-white shadow-xl shadow-emerald-500/20 hover:bg-emerald-600 hover:scale-105 transition-all active:scale-95 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-[18px]">done_all</span>
                                            Tout valider
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-4">
                                {items.map(item => {
                                    const fullyReceived = item.currentReceivedQty >= item.quantity;
                                    const hadPreviousReception = item.previouslyReceived > 0;
                                    const itemDiff = item.currentReceivedQty - item.previouslyReceived;

                                    return (
                                        <div key={item.id} className={`flex items-center gap-6 p-6 rounded-3xl border transition-all duration-300 ${fullyReceived && !isCorrecting ? 'bg-emerald-500/5 dark:bg-emerald-900/10 border-emerald-500/20 shadow-lg shadow-emerald-500/5' : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-800 hover:border-primary/30 shadow-sm hover:shadow-xl group'}`}>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-black text-sm text-slate-900 dark:text-white leading-tight mb-1 truncate group-hover:text-primary transition-colors">
                                                    {item.product?.name || item.catalogItem?.name || 'Article Inconnu'}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-black font-mono text-slate-400 bg-slate-50 dark:bg-slate-900 px-2 py-0.5 rounded leading-none uppercase tracking-widest">
                                                        {item.product?.sku || item.catalogItem?.sku || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-6 shrink-0 h-10">
                                                <div className="text-right">
                                                    <p className="text-[8px] uppercase font-black text-slate-400 mb-0.5 tracking-widest">Commandé</p>
                                                    <p className="font-black text-base text-slate-700 dark:text-slate-300 font-mono leading-none">{item.quantity}</p>
                                                </div>

                                                {hadPreviousReception && (
                                                    <div className="text-right px-4 border-l border-slate-100 dark:border-slate-800 h-full flex flex-col justify-center">
                                                        <p className="text-[8px] uppercase font-black text-primary mb-0.5 tracking-widest">Reçu</p>
                                                        <p className="font-black text-base text-primary dark:text-primary/80 font-mono leading-none">{item.previouslyReceived}</p>
                                                    </div>
                                                )}

                                                <div className="w-32 border-l border-slate-100 dark:border-slate-800 pl-6 h-full flex items-center">
                                                    {isReadOnly || (fullyReceived && !isCorrecting) ? (
                                                        <div className="w-full flex items-center justify-center gap-2">
                                                            <span className="font-black text-lg text-emerald-600 dark:text-emerald-400 font-mono">{item.currentReceivedQty}</span>
                                                            <span className="material-symbols-outlined text-emerald-500 size-5 flex items-center justify-center text-[18px]">verified</span>
                                                        </div>
                                                    ) : (
                                                        <div className="relative w-full group/input">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max={item.quantity}
                                                                value={item.currentReceivedQty}
                                                                onChange={e => updateReceivedQty(item.id, parseInt(e.target.value) || 0)}
                                                                className={`w-full h-10 px-3 pr-10 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-2 outline-none font-black text-center font-mono text-sm transition-all ${itemDiff < 0 ? 'border-amber-200 focus:border-amber-500 text-amber-700' : 'border-slate-100 dark:border-slate-800 focus:border-primary text-slate-900 dark:text-white'}`}
                                                            />
                                                            {itemDiff !== 0 && (
                                                                <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-black pointer-events-none ${itemDiff > 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
                                                                    {itemDiff > 0 ? '+' : ''}{itemDiff}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Footer (Fixed) Premium */}
                        {!isReadOnly ? (
                            <form onSubmit={handleSubmit} className="px-10 py-8 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30 shrink-0">
                                <div className="flex flex-wrap items-end gap-10">
                                    <div className="flex-1 min-w-[200px] space-y-2">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Date de réception</label>
                                        <div className="relative group">
                                            <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-primary/10 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                                <span className="material-symbols-outlined text-[18px]">calendar_today</span>
                                            </div>
                                            <input
                                                type="date"
                                                value={deliveryDate}
                                                onChange={(e) => setDeliveryDate(e.target.value)}
                                                required
                                                className="w-full h-12 pl-14 pr-4 rounded-xl bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 outline-none focus:border-primary font-black text-sm transition-all"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-[220px] space-y-2">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Destination Stock</label>
                                        <div className="relative group">
                                            <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-primary/10 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                                <span className="material-symbols-outlined text-[18px]">location_on</span>
                                            </div>
                                            <select
                                                value={selectedWarehouseId}
                                                onChange={(e) => setSelectedWarehouseId(e.target.value)}
                                                required
                                                className="w-full h-12 pl-14 pr-10 rounded-xl bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 outline-none focus:border-primary font-black text-sm transition-all appearance-none"
                                            >
                                                <option value="">Sélectionner un entrepôt...</option>
                                                {warehouses.map(w => (
                                                    <option key={w.id} value={w.id}>{w.name}</option>
                                                ))}
                                            </select>
                                            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">expand_more</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 ml-auto">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-8 h-12 rounded-xl text-slate-500 font-black hover:bg-slate-200 dark:hover:bg-slate-800 transition-all uppercase text-[10px] tracking-[0.2em]"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={!hasChanges}
                                            className="flex items-center gap-3 px-10 h-12 rounded-xl bg-primary text-white font-black hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98] uppercase text-[10px] tracking-[0.2em]"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">verified</span>
                                            {allFullyReceived ? 'Clôturer la commande' : 'Valider la réception'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        ) : (
                            <div className="px-10 py-8 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30 flex justify-end shrink-0">
                                <button
                                    onClick={onClose}
                                    className="px-10 h-12 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black hover:opacity-90 transition-all shadow-xl uppercase text-[10px] tracking-[0.2em] flex items-center gap-3"
                                >
                                    <span className="material-symbols-outlined text-[20px]">close_fullscreen</span>
                                    Fermer
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PurchaseOrderReceptionModal;
